/**
 * Institutional-framework text fields — program/department `purpose`, mission —
 * are typed as `string`, but legacy/admin data may store them as a
 * `{ text, scripture }` object (the mission shape) or as a TipTap JSON document.
 * Rendering either object form directly as a React / @react-pdf child throws
 * ("Objects are not valid as a React child", React error #31), which crashed the
 * portal Syllabus tab and the syllabus PDF download in production.
 *
 * `frameworkText` coerces any of these shapes to a plain display string so the
 * value never reaches the renderer as a raw object.
 */
export function frameworkText(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  const obj = value as {
    text?: unknown;
    scripture?: unknown;
    type?: unknown;
    content?: unknown;
  };

  // TipTap JSON document → flatten to text paragraphs.
  if (obj.type === 'doc' || Array.isArray(obj.content)) {
    return tipTapToText(value as Record<string, unknown>);
  }

  // Institutional `{ text, scripture? }` shape. `text` is usually a string but
  // drifted data can nest it (an object or a TipTap doc), so resolve it
  // recursively rather than assuming a string.
  if ('text' in obj) {
    const text = frameworkText(obj.text);
    const scripture =
      typeof obj.scripture === 'string' && obj.scripture ? ` (${obj.scripture})` : '';
    return text + scripture;
  }

  return '';
}

/**
 * True for the institutional `{ text, scripture? }` shape — an object whose keys
 * are only `text` (and optionally `scripture`). `text`'s type is NOT required to
 * be a string: drifted data sometimes nests it, and rendering ANY such object
 * raw throws React error #31, so we still want to coerce it.
 */
function isFrameworkObject(v: unknown): v is { text: unknown; scripture?: unknown } {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return 'text' in o && Object.keys(o).every((k) => k === 'text' || k === 'scripture');
}

/**
 * Deep-coerce every `{ text, scripture }` object anywhere in a value to a plain
 * string (via {@link frameworkText}), leaving all other structure intact. Use it
 * to sanitize a whole syllabus payload before rendering so a drifted field
 * (a `purpose`/note stored in the mission shape) can never reach the renderer as
 * a raw object — without having to know which field drifted.
 *
 * NOTE: it also coerces a top-level `mission`; callers that read `mission.text`
 * must re-attach the original object afterwards (see SyllabusPdfDocument).
 */
export function deepCoerceFrameworkText<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => deepCoerceFrameworkText(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    if (isFrameworkObject(value)) return frameworkText(value) as unknown as T;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepCoerceFrameworkText(v);
    }
    return out as T;
  }
  return value;
}

/**
 * Normalize a `mission` value to `{ text: string; scripture? }`.
 *
 * Mission is meant to be `{ text: string, scripture?: string }`, but drifted
 * tenant data nests it — e.g. `{ text: { text, scripture } }` — so `mission.text`
 * is an object and rendering it raw throws React error #31. This descends through
 * nested `text` wrappers, picks up the first `scripture` it finds, and returns a
 * flat object whose `text` is always a string.
 */
export function normalizeMission(value: unknown): { text: string; scripture?: string } | undefined {
  if (!value || typeof value !== 'object') return undefined;
  let scripture: string | undefined;
  let cur: unknown = value;
  while (cur && typeof cur === 'object') {
    const o = cur as { text?: unknown; scripture?: unknown };
    if (scripture === undefined && typeof o.scripture === 'string' && o.scripture) {
      scripture = o.scripture;
    }
    if (typeof o.text === 'string') {
      return scripture !== undefined ? { text: o.text, scripture } : { text: o.text };
    }
    cur = o.text;
  }
  const text = frameworkText(value);
  if (!text) return undefined;
  return scripture !== undefined ? { text, scripture } : { text };
}

/** Minimal TipTap-JSON → plain text (paragraphs joined by blank lines). */
function tipTapToText(doc: Record<string, unknown>): string {
  const out: string[] = [];
  const walk = (node: unknown): string => {
    if (!node || typeof node !== 'object') return '';
    const n = node as { type?: string; text?: string; content?: unknown[] };
    if (n.type === 'text' && typeof n.text === 'string') return n.text;
    if (Array.isArray(n.content)) return n.content.map(walk).join('');
    return '';
  };
  for (const node of (doc as { content?: unknown[] }).content ?? []) {
    const text = walk(node).trim();
    if (text) out.push(text);
  }
  return out.join('\n\n');
}

/** True when a rich-text (TipTap) document has any visible text. A section can
 *  EMPTY a field on purpose ("quitar información"); that is stored as an empty
 *  doc — `null` would clear the override and bring the template's text back —
 *  so every renderer has to treat an empty doc as absent, heading included. */
export function hasRichText(doc: unknown): boolean {
  if (!doc || typeof doc !== 'object') return false;
  const text = (node: unknown): string => {
    const n = node as { text?: unknown; content?: unknown };
    const own = typeof n.text === 'string' ? n.text : '';
    const kids = Array.isArray(n.content) ? n.content.map(text).join('') : '';
    return own + kids;
  };
  return text(doc).trim() !== '';
}
